import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { AnimateSharedLayout, AnimatePresence, motion } from 'framer-motion'

// Actions
import { actions as authActions } from 'modules/auth/actions'
import { actions as trackingActions } from 'modules/tracking/actions'

// Components
import InteractiveGuide, { trackId } from 'components/onboard/interactiveguide'
import PageDots from 'components/onboard/pagedots'
import { Button } from '@tracklib/kit'
import Markdown from 'components/base/markdown'

// Hooks
import { useRequest } from 'modules/requests/hooks'
import { useSubscription } from 'modules/subscription/hooks'

import styles from './onboarddesktop.css'

const sharedTransition = {
  repeatType: 'loop',
  delay: 1,
  ease: 'easeInOut',
  repeat: Infinity,
}
const arrow = {
  hidden: {
    pathLength: 0,
    opacity: 0,
  },
  visible: {
    pathLength: 1,
    opacity: 1,
    transition: {
      default: {
        duration: 2.5,
        repeatDelay: 1,
        ...sharedTransition,
      },
      opacity: {
        duration: 0.2,
        repeatDelay: 1 + 2.3,
        ...sharedTransition,
      },
    },
  },
}

const OnboardDesktop = ({ redirect }: { redirect?: string }): JSX.Element => {
  const subscription = useSubscription()
  const isOnTrial = subscription?.state === 'TRIAL'

  const pages = [
    {
      title: 'It’s sampling time.',
      description: `${
        isOnTrial ? '__Your trial has now started.__' : ''
      } Welcome to your free trial of Tracklib. You now have access to our full catalog of tracks, ready for you to loop, download, chop, sample and make your own.`,
      id: 1,
    },
    {
      title: 'Find the perfect loop',
      description:
        'Use the loop function to create and download seamless loops from any track. Try out different loops by clicking other parts of the song.',
      id: 2,
    },
    {
      title: 'Find the perfect loop',
      description:
        'Use the loop function to create and download seamless loops from any track. Try out different loops by clicking other parts of the song.',
      id: 3,
    },
    {
      title: 'Favorite, curate and download',
      description:
        'Save your favorite tracks, curate collections and download seamless loops or full tracks in master quality with only one click for a single credit.',
      id: 4,
    },
    {
      title: 'Let the music guide you',
      description:
        'Explore our specially curated catalog for inspiration or dig deep by browsing and filtering our vast library of tracks, artists, labels and genres.',
      id: 5,
    },
  ]

  const dispatch = useDispatch()
  const [page, setPage] = useState(0)
  const { title, description } = pages[page]

  const query = `ids=${trackId}`
  const request = useRequest('FETCH_TRACKS_BY_SEARCH_QUERY', query)

  const paginate = () => {
    dispatch(trackingActions.trackevent('Onboarding paginate', { currentPage: page, newPage: page + 1, type: 'desktop' }))
    return setPage(p => p + 1)
  }

  const pathProps = {
    stroke: '#e4e4e4',
    variants: arrow,
    initial: 'hidden',
    animate: 'visible',
    strokeWidth: '1.5',
    fill: 'none',
  }

  const SVG = () => {
    return (
      <motion.svg
        width="381px"
        height="91px"
        viewBox="0 0 381 91"
        xmlns="http://www.w3.org/2000/svg"
        style={{ width: 'min(28vw, 381px)' }}
        preserveAspectRatio="none"
      >
        <motion.path d="M0.851196289,88.4663696 C89.1721497,88.4663696 254.603841,62.3508301 380,0" {...pathProps} />
      </motion.svg>
    )
  }

  const SVG2 = () => {
    return (
      <motion.svg
        width="350px"
        height="133px"
        viewBox="0 0 358 133"
        xmlns="http://www.w3.org/2000/svg"
        className={styles.svg2}
        preserveAspectRatio="none"
      >
        <motion.path d="M0.851196289,130.46637 C89.1721497,130.46637 267.204895,93.9860229 357,2.27373675e-13" {...pathProps} />
      </motion.svg>
    )
  }

  const SVG3 = () => {
    return (
      <motion.svg viewBox="0 0 332 161" xmlns="http://www.w3.org/2000/svg" className={styles.svg3} preserveAspectRatio="none">
        <motion.path
          d="M0,154.061383 C39.0590502,161.996449 72.26617,160.369333 99.6213595,149.180036 C126.976549,137.990739 153.689189,116.268876 179.75928,84.0144463 C202.881134,58.6141467 225.271616,40.0455118 246.930726,28.3085416 C268.589835,16.5715715 296.279594,7.1353909 330,0"
          {...pathProps}
        />
      </motion.svg>
    )
  }

  const getAction = () => {
    if (page === 0) {
      return (
        <p className={styles.handWritten}>
          Play your first track to get started
          <SVG />
        </p>
      )
    }
    if (page === 1) {
      return (
        <p className={styles.handWritten}>
          Click the loop icon to try the loop mode <SVG2 />
        </p>
      )
    }
    if (page === 2) {
      return (
        <Button size="large" onClick={paginate}>
          Continue
        </Button>
      )
    }
    if (page === 3) {
      return (
        <p className={styles.handWritten}>
          Favorite your first track to continue <SVG3 />
        </p>
      )
    }
    if (page === 4) {
      return (
        <Button
          size="large"
          to={redirect || '/inspiration'}
          onClick={() => {
            dispatch(trackingActions.trackevent('Finished onboarding'))
            return dispatch(authActions.onboardingCompleted())
          }}
        >
          Start browsing
        </Button>
      )
    }
    return null
  }

  return (
    <div className={styles.bg}>
      <motion.div
        className={styles.modal}
        initial={{ opacity: 0, scale: 0.95, y: 35, perspective: 550, rotateX: -3 }}
        animate={request.isDone ? { opacity: 1, scale: 1, y: 0, perspective: 550, rotateX: 0 } : {}}
        transition={{ ease: [0.33, 0.52, 0.4, 0.99], duration: 1.2, delay: 0.5 }}
      >
        <AnimateSharedLayout>
          <div className={styles.wrapper}>
            <div className={styles.contentWrapper}>
              <AnimatePresence initial={false}>
                <motion.div
                  className={styles.content}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  key={pages[page].title}
                  transition={{
                    x: { type: 'spring', stiffness: 300, damping: 30 },
                    opacity: { duration: 0.1 },
                  }}
                >
                  <h2 className={styles.title}>
                    <Markdown>{title}</Markdown>
                  </h2>
                  <p className={styles.description}>
                    <Markdown>{description}</Markdown>
                  </p>

                  <motion.nav className={styles.nav}>
                    <div className={styles.buttonWrapper}>{getAction()}</div>
                  </motion.nav>
                </motion.div>
              </AnimatePresence>
            </div>
            <PageDots pages={pages} current={page} className={styles.pageDots} />
            <aside className={styles.media}>
              <InteractiveGuide page={page} paginate={paginate} />
            </aside>
          </div>
        </AnimateSharedLayout>
      </motion.div>
    </div>
  )
}

export default OnboardDesktop
